import { useRef, useEffect, useMemo, useState } from "react";
import { VideoTile } from "./VideoTile";
import { RailTiles } from "./RailTiles";
import type { Participant } from "@vc/models";
import type { BackgroundImage } from "@vc/data/backgroundCategories";
import { Maximize2, Minimize2, Pause, Play, RotateCcw, RotateCw, VolumeX, X } from "lucide-react";
import { useTranslation } from "@/clientWeb/i18n/LanguageContext";
import { useFullscreen } from "@vc/viewmodels/screen/useFullscreen";
import { usePlayableUrl } from "@/features/meeting/hooks/usePlayableUrl";
import { isIOSDevice } from "@vc/viewmodels/hooks/useDeviceDetection";
import type { SharedMediaCommand, SharedMediaControlAction } from "@/features/meeting/hooks/useSharedMedia";
import { isYouTubeEmbed, useYouTubePlayer } from "@/features/meeting/hooks/useYouTubePlayer";

interface VideoShareLayoutProps {
  videoName: string;
  videoUrl: string;
  /** "embed" renders an iframe (YouTube / Vimeo / Drive are pages, not media
   *  files). Anything else — including undefined, for entries stored before
   *  this existed — renders a <video>. */
  videoKind?: "file" | "embed";
  /** Epoch ms at which the room started playing this, from the share
   *  broadcast. Absent for a local-only share, which nobody else sees. */
  startedAt?: number;
  /** True when this share cannot reach other participants (a blob: URL from a
   *  local upload). Shown as a banner so the presenter is not left believing
   *  the room can see it. */
  localOnly?: boolean;
  /** May this viewer stop the presentation? Presenter + moderators only. */
  canClose?: boolean;
  /**
   * Is this client the one presenting?
   *
   * Playback belongs to whoever shared the video: they get the controls and
   * everyone else follows. Two people driving one video means each one's seek
   * yanks the other back, so the room ends up watching a fight rather than a
   * film. A moderator can still close the presentation — that is `canClose`.
   */
  isPresenter?: boolean;
  /** Sends a playback instruction to the room. Presenter only. `soft` marks a
   *  drift correction, which viewers apply only if they have fallen out of
   *  step. */
  onControl?: (action: SharedMediaControlAction, position: number, soft?: boolean) => void;
  /** The presenter's latest instruction, for a viewer to apply. */
  command?: SharedMediaCommand | null;
  /**
   * The presenter's player reports where it has reached, so a participant who
   * joins mid-presentation can be dropped at the same point.
   */
  onProgress?: (position: number, paused: boolean) => void;
  /** True when the room's playback is stopped — a late joiner must not run on
   *  from where they landed. */
  startPaused?: boolean;
  presenterName: string;
  /** Emoji for one participant, or null. Per-peer: a reaction
   *  belongs to whoever sent it, not to every tile on screen. */
  reactionFor: (peerId: string | undefined | null) => string | null;
  pinnedIds: string[];
  railRowChunks: Participant[][];
  rightRailWidth: number;
  rightRailPageCount: number;
  safeRightRailPage: number;
  railRowH: number;
  onPin: (participantId: string) => void;
  /** Moderator only: pin a tile on everyone's screen. */
  onPinForAll?: (participantId: string) => void;
  pinnedForAllId?: string | null;
  onMaximize: (element: HTMLElement) => void;
  onOpenChat: (participantId: string) => void;
  setRightRailEl: (node: HTMLDivElement | null) => void;
  /** Phone layout: no desktop participant rail — the caller shows people in a strip. */
  phoneLayout?: boolean;
  onSetRightRailPage: (page: number | ((prev: number) => number)) => void;
  selectedBackground: BackgroundImage | null;
  onClose: () => void;
  railDragRef?: React.MutableRefObject<{ startX: number; startWidth: number } | null>;
  /** Mic and camera buttons, shown in the header only while this layout owns
   *  full screen — the meeting's control bar is outside it and not painted. */
  fullscreenControls?: React.ReactNode;
}

/**
 * Append `start=<seconds>` to an embed URL, preserving its existing query.
 *
 * `enablejsapi=1` rides along unconditionally: without it the player ignores
 * postMessage, so the presenter's pause/resume/skip would reach every viewer's
 * iframe and be silently discarded.
 */
function embedSrcWithStart(url: string, offsetSeconds: number, presenter: boolean): string {
  const sep = url.includes("?") ? "&" : "?";
  let out = url.includes("enablejsapi=") ? url : `${url}${sep}enablejsapi=1`;
  // YouTube refuses postMessage commands unless the embed names the page it
  // will accept them from. Without this the presenter's pause and resume were
  // delivered to every viewer's player and dropped on the floor, which is why
  // only the presenter's own copy responded.
  if (!out.includes("origin=") && typeof window !== "undefined") {
    out += `&origin=${encodeURIComponent(window.location.origin)}`;
  }
  // A viewer gets no player chrome. YouTube's own controls sit inside the
  // iframe and cannot be reached from outside, so the only way to stop a
  // viewer scrubbing their copy out of step with the room is to ask the player
  // not to draw them. `disablekb` closes the keyboard shortcuts that would
  // otherwise do the same thing.
  if (!presenter && !out.includes("controls=")) out += "&controls=0&disablekb=1";
  if (offsetSeconds <= 0) return out;
  return `${out}&start=${offsetSeconds}`;
}


export function VideoShareLayout({
  videoName,
  videoUrl,
  videoKind = "file",
  startedAt,
  localOnly = false,
  canClose = true,
  isPresenter = true,
  onControl,
  command = null,
  onProgress,
  startPaused = false,
  presenterName,
  reactionFor,
  pinnedIds,
  railRowChunks,
  rightRailWidth,
  rightRailPageCount,
  safeRightRailPage,
  railRowH,
  onPin,
  onPinForAll,
  pinnedForAllId,
  onMaximize,
  onOpenChat,
  setRightRailEl,
  phoneLayout = false,
  onSetRightRailPage,
  selectedBackground,
  onClose,
  railDragRef,
  fullscreenControls,
}: VideoShareLayoutProps) {
  const t = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  // We target the *main* container (not the layout root) so going fullscreen
  // expands the player only — the participant rail stays out of the way
  // because it is rendered as a sibling outside this ref.
  const mainRef = useRef<HTMLDivElement>(null);
  const { isFullscreen, exit: exitFullscreen, toggle: toggleFullscreen } = useFullscreen(mainRef);

  // A failed <video> load is otherwise completely silent — the element just
  // renders black. Surfacing the MediaError turns "the link does nothing" into
  // an answerable question.
  const [mediaError, setMediaError] = useState<string | null>(null);
  // Autoplay with sound refused (iOS always, other browsers before a tap):
  // `mutedForAutoplay` — playing silently, offer to unmute; `needsTap` — not
  // even muted playback was allowed, so a tap has to start it.
  const [mutedForAutoplay, setMutedForAutoplay] = useState(false);
  const [needsTap, setNeedsTap] = useState(false);

  /**
   * Start playback as far as this browser allows.
   *
   * A viewer's play() is not a user gesture, and iOS refuses it with sound.
   * That used to be swallowed ("Autoplay might be blocked, that's ok"), and
   * the viewer has no controls — so on an iPhone the shared video simply sat
   * there, looking like it was still loading. Muted playback is allowed
   * inline; failing that, a tap button.
   */
  /** Set once the viewer has tapped for sound — later instructions keep it. */
  const unmutedByUserRef = useRef(false);

  const isRefusal = (err: unknown) => (err as { name?: string } | null)?.name === "NotAllowedError";

  const startPlayback = (el: HTMLVideoElement) => {
    // An iPhone viewer starts muted outright. iOS never plays a viewer's video
    // with sound without a tap, so asking with sound first only bought a
    // refusal and a second attempt — and that second attempt, when the room's
    // own sync (a pause, a seek) interrupted it, read as "even muted playback
    // is refused" and put Tap to play over a video that would have played.
    // Not once they have tapped for sound: WebKit lets that element play
    // unmuted from then on, and re-muting them on every instruction would be
    // worse than the bug.
    if (!isPresenter && isIOSDevice() && !unmutedByUserRef.current && !el.muted) {
      el.muted = true;
      setMutedForAutoplay(true);
    }
    const playMuted = () => {
      el.muted = true;
      void el.play().then(
        () => { setMutedForAutoplay(true); setNeedsTap(false); },
        // Only a refusal needs the tap. An AbortError is this play being
        // interrupted — by a pause the room asked for, or a new source — and
        // the next instruction starts it again.
        (err: unknown) => { if (isRefusal(err)) setNeedsTap(true); },
      );
    };
    void el.play().then(
      () => {
        setNeedsTap(false);
        // Playing, but silently — say so, or the viewer has no way to sound.
        if (el.muted && !isPresenter && !unmutedByUserRef.current) setMutedForAutoplay(true);
      },
      (err: unknown) => {
        if (!isRefusal(err)) return;
        if (el.muted) setNeedsTap(true);
        else playMuted();
      },
    );
  };

  // An uploaded file lives behind the backend's bearer auth, which a <video>
  // element cannot present. This resolves it to a local object URL under this
  // client's own identity; public links pass through untouched.
  const { playableUrl, loading: resolving, error: resolveError } = usePlayableUrl(videoUrl, videoName);

  /**
   * How far into the video the room is, in seconds.
   *
   * Everyone starts from the same instant — `startedAt` travels on the share
   * broadcast — so a participant who joins halfway through lands halfway
   * through. Without this they began at zero while the room was minutes ahead,
   * watching a different part of the same video and seeing reactions to
   * something they had not reached.
   *
   * This is only the STARTING point, and it assumes uninterrupted playback —
   * it drifts by however long the presenter paused. The correction is the
   * resync the presenter sends when someone joins, which carries the player's
   * true position and lands the joiner on it.
   */
  const roomOffsetSeconds = (): number => {
    if (!startedAt) return 0;
    return Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
  };

  /**
   * The embed URL, resolved ONCE per video.
   *
   * `start` is computed from `Date.now()`, so building this inline handed the
   * iframe a different `src` on every render — and changing an iframe's src
   * reloads it. The player restarted continually: black frames, audio cutting
   * in and out, playback never settling. It is the join offset, which is a
   * property of arriving, not something to re-evaluate while watching.
   */
  const embedSrc = useMemo(
    () =>
      embedSrcWithStart(
        playableUrl,
        startedAt ? Math.max(0, Math.floor((Date.now() - startedAt) / 1000)) : 0,
        isPresenter,
      ),
    // Deliberately not depending on anything time-based: recomputing this is
    // exactly the bug.
     
    [playableUrl, startedAt, isPresenter],
  );

  useEffect(() => {
    setMediaError(null);
    setMutedForAutoplay(false);
    setNeedsTap(false);
    if (videoKind === "embed") return;
    const el = videoRef.current;
    if (!el || !playableUrl || playableUrl === "#") return;
    // A new video starts from scratch: the <video> may be the same element,
    // still muted from the last one, and a tap for sound was for that video.
    el.muted = false;
    unmutedByUserRef.current = false;

    // Seek before playing. Setting currentTime on a element with no metadata
    // yet is silently dropped, so wait for the duration to be known — and cap
    // at it, since a video shared long ago would otherwise seek past the end
    // and sit on a black frame.
    const seekThenPlay = () => {
      const offset = roomOffsetSeconds();
      if (offset > 0 && Number.isFinite(el.duration) && offset < el.duration) {
        el.currentTime = offset;
      }
      // The share said the room is paused, so stay where we landed rather than
      // playing on alone. Arriving during a pause otherwise put this viewer
      // ahead of everyone else the moment they joined.
      if (startPaused) {
        el.pause();
        return;
      }
      startPlayback(el);
    };

    if (el.readyState >= 1) seekThenPlay();
    else {
      el.addEventListener("loadedmetadata", seekThenPlay, { once: true });
      // iOS does not load a video's metadata until it is asked to play, so
      // waiting for `loadedmetadata` first waited forever. Ask now; the seek
      // still lands when the metadata arrives.
      if (isIOSDevice() && !startPaused) startPlayback(el);
    }
    return () => el.removeEventListener("loadedmetadata", seekThenPlay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playableUrl, videoKind, startedAt, startPaused]);

  /**
   * Set while applying an instruction from the room.
   *
   * Calling play()/pause() or assigning currentTime fires the same events a
   * person clicking would, so without this a viewer applying the presenter's
   * pause would broadcast a pause of its own — and on a slow link the two
   * bounce off each other indefinitely.
   */
  const applyingRef = useRef(false);

  const emit = (action: SharedMediaControlAction, position: number) => {
    if (applyingRef.current || !isPresenter) return;
    onControl?.(action, position);
  };

  /**
   * Where the embed has reached, in seconds — an estimate.
   *
   * A cross-origin iframe will not tell us its position, so skipping a YouTube
   * video has to be computed rather than read: the join offset, plus wall time
   * spent playing. It is right to within a second or two of what the presenter
   * sees, which is what skip-by-ten needs. A <video> element is asked directly
   * and needs none of this.
   */
  const embedPosRef = useRef({
    base: startedAt ? Math.max(0, Math.floor((Date.now() - startedAt) / 1000)) : 0,
    since: Date.now(),
    playing: !startPaused,
  });
  const embedPosition = (): number => {
    const p = embedPosRef.current;
    return p.playing ? p.base + Math.floor((Date.now() - p.since) / 1000) : p.base;
  };
  const setEmbedPosition = (seconds: number, playing: boolean) => {
    embedPosRef.current = { base: Math.max(0, seconds), since: Date.now(), playing };
  };

  /** YouTube and Vimeo both take commands this way, given `enablejsapi=1`. */
  const embedRef = useRef<HTMLIFrameElement>(null);
  const postToEmbed = (func: string, args: unknown[] = []) => {
    const win = embedRef.current?.contentWindow;
    if (!win) return;
    win.postMessage(JSON.stringify({ event: "command", func, args }), "*");
  };

  /**
   * Announce this page to the player.
   *
   * The API opens a channel on this handshake; a command sent before it is
   * ignored, so the very first pause after a share often did nothing. Sent on
   * every load, and once more shortly after, because "loaded" is the iframe
   * document rather than the player inside it.
   */
  const handshakeEmbed = () => {
    const win = embedRef.current?.contentWindow;
    if (!win) return;
    const hello = JSON.stringify({ event: "listening", id: 1, channel: "widget" });
    win.postMessage(hello, "*");
    setTimeout(() => embedRef.current?.contentWindow?.postMessage(hello, "*"), 600);
  };

  /**
   * A YouTube embed is driven through the official API instead — see
   * useYouTubePlayer for why postMessage is not good enough. Vimeo, Drive and
   * anything else keep the postMessage path above.
   */
  const usesApi = videoKind === "embed" && isYouTubeEmbed(playableUrl);

  // A state change caused by a command we just applied must not be reported
  // back as a fresh one. Wider than a tick: the player answers asynchronously,
  // well after the call that provoked it.
  const suppressUntilRef = useRef(0);

  const yt = useYouTubePlayer({
    iframeRef: embedRef,
    enabled: usesApi,
    srcKey: embedSrc,
    onStateChange: (playing, position) => {
      // The presenter driving YouTube's own controls should move the room just
      // as our buttons do. A viewer reports nothing — their player only ever
      // follows, and their controls are covered anyway.
      if (!isPresenter || Date.now() < suppressUntilRef.current) return;
      // Read from the player — the whole point of using the API — so it
      // travels as a position and carries a scrubber skip with it. Unless the
      // player is answering 0 mid-buffer, in which case the verb goes on its
      // own rather than dragging everybody to the start.
      const at = sane(position);
      setEmbedPosition(at ?? localPosition(), playing);
      onControl?.(playing ? "play" : "pause", at ?? UNKNOWN_POSITION);
    },
  });

  /**
   * Where playback has reached — or null when nothing can actually be asked.
   *
   * The distinction matters more than the number. A position is broadcast so
   * every other player can be moved to it, so a guess is not a worse answer,
   * it is a wrong one: the estimate knows nothing about the presenter dragging
   * YouTube's own scrubber, and sending it as fact teleported the whole room to
   * a point the presenter left minutes ago. When the player cannot be read,
   * this says so and the instruction travels without a position.
   */
  // The last reading believed to be real, used to recognise a bogus one.
  const lastGoodPosRef = useRef(0);

  /**
   * Reject a reading that says "the very beginning" when we were well past it.
   *
   * The player answers 0 for a moment while it buffers or re-seeks, and that
   * moment is exactly when a state change fires. Taken at face value it
   * broadcasts "play from 0" and sends the whole room back to the start of the
   * video — which is what everyone was seeing on resume. Treated as unknown,
   * the instruction still travels; it simply carries no position, so everyone
   * resumes where they already were.
   */
  const sane = (read: number | null): number | null => {
    if (read === null || !Number.isFinite(read)) return null;
    if (read < 1 && lastGoodPosRef.current > 5) return null;
    lastGoodPosRef.current = read;
    return read;
  };

  const trustedPosition = (): number | null => {
    if (videoKind !== "embed") {
      const t = videoRef.current?.currentTime;
      return sane(typeof t === "number" ? t : null);
    }
    return sane(yt.position());
  };

  /** Where this client's playback has reached; the estimate as a last resort,
   *  used only for local decisions, never sent to anyone. */
  const localPosition = (): number => {
    if (videoKind !== "embed") return videoRef.current?.currentTime ?? 0;
    return yt.position() ?? embedPosition();
  };

  /** Position meaning "I could not read it" on the wire. */
  const UNKNOWN_POSITION = -1;

  /** Apply one instruction locally, whoever it came from. */
  const applyCommand = (action: SharedMediaControlAction, position: number) => {
    applyingRef.current = true;
    try {
      if (videoKind === "embed") {
        // Anything the player does in response is ours, not the presenter
        // acting — do not send it back out as a new instruction.
        suppressUntilRef.current = Date.now() + 1500;

        if (usesApi && yt.ready) {
          if (action === "seek") {
            yt.seekTo(position);
            setEmbedPosition(position, embedPosRef.current.playing);
          } else if (action === "pause") {
            yt.pause();
            setEmbedPosition(position, false);
          } else {
            yt.play();
            setEmbedPosition(position, true);
          }
          return;
        }

        // Fallback for a non-YouTube embed, or a player that has not finished
        // booting.
        if (action === "seek") {
          postToEmbed("seekTo", [position, true]);
          setEmbedPosition(position, embedPosRef.current.playing);
        } else if (action === "pause") {
          postToEmbed("pauseVideo");
          setEmbedPosition(position, false);
        } else {
          postToEmbed("playVideo");
          setEmbedPosition(position, true);
        }
        return;
      }
      const el = videoRef.current;
      if (!el) return;
      if (action === "seek") {
        if (Number.isFinite(el.duration) && position < el.duration) el.currentTime = position;
      } else if (action === "pause") {
        el.pause();
      } else {
        // A viewer's tab may block autoplay with sound — muted, or a tap.
        startPlayback(el);
      }
    } finally {
      // Released on the next tick: the events these calls provoke are queued,
      // not synchronous, so clearing here would let them through as fresh
      // instructions.
      setTimeout(() => {
        applyingRef.current = false;
      }, 0);
    }
  };

  /**
   * Re-apply the room's last instruction once the player can hear it.
   *
   * A joiner is sent a resync as soon as they arrive, which is usually before
   * the YouTube player has finished booting — the command was applied to a
   * player that was not listening yet and the video carried on from wherever
   * the embed happened to start. Replaying it on ready is what actually puts
   * a late joiner at the room's position.
   */
  const lastCommandRef = useRef<SharedMediaCommand | null>(null);
  lastCommandRef.current = command ?? lastCommandRef.current;
  useEffect(() => {
    if (!yt.ready || isPresenter) return;
    const last = lastCommandRef.current;
    if (!last) return;
    applyCommand("seek", last.position);
    applyCommand(last.paused || last.action === "pause" ? "pause" : "play", last.position);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yt.ready, isPresenter]);

  // Viewers follow the presenter. Keyed on `seq` so the same instruction is
  // never applied twice and an out-of-order one is dropped by the hook.
  const appliedSeqRef = useRef(-1);
  useEffect(() => {
    if (!command || isPresenter) return;
    if (command.seq <= appliedSeqRef.current) return;
    appliedSeqRef.current = command.seq;

    // A position of -1 means the presenter could not read their player. Follow
    // the play/pause and stay where we are: moving to a made-up point is how
    // the room ended up back at the beginning.
    const positioned = command.position >= 0;

    if (command.soft) {
      // A correction sent because somebody joined. Anyone already in step
      // ignores it — jumping the playhead a fraction of a second every time a
      // participant arrives is worse than the drift it would fix.
      if (positioned && Math.abs(localPosition() - command.position) > 3) {
        applyCommand("seek", command.position);
      }
      applyCommand(command.paused ? "pause" : "play", command.position);
      return;
    }

    // Position is honoured on EVERY instruction, not only on a seek. YouTube
    // reports no seek event at all: dragging its scrubber surfaces as a state
    // change, so the presenter skipping ahead arrived as a bare "play" and
    // every viewer carried on from where they already were. Anything more than
    // a couple of seconds out is pulled into line first.
    if (positioned && Math.abs(localPosition() - command.position) > 2) {
      applyCommand("seek", command.position);
    }
    applyCommand(command.action, command.position);
    // applyCommand is recreated each render but reads only refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [command, isPresenter, videoKind]);

  // Reported about once a second while presenting. Cheap — it writes a ref in
  // the parent and renders nothing — and it is what makes a late joiner land
  // where the room actually is rather than where the video started.
  useEffect(() => {
    if (!isPresenter || !onProgress) return;
    const tick = () => {
      // Only a real reading. A joiner is placed at whatever this says, so
      // reporting an estimate would drop them wherever the arithmetic drifted
      // to; saying nothing leaves them on the share's own timestamp, which is
      // at least honest about what it knows.
      const at = trustedPosition();
      if (at === null) return;
      const paused = videoKind === "embed" ? (yt.paused() ?? false) : !!videoRef.current?.paused;
      onProgress(at, paused);
    };

    // Every few seconds the presenter also tells the room where it should be.
    // This is the safety net under an embed: the player announces play and
    // pause but never a seek, so without a periodic correction a skip made
    // with YouTube's own scrubber would only reach everyone if it happened to
    // change the play state. Viewers within a few seconds ignore it, so in the
    // ordinary case this costs a message and changes nothing.
    let sinceSync = 0;
    const syncTick = () => {
      if (videoKind !== "embed") return;
      sinceSync += 1;
      if (sinceSync < 4) return;
      sinceSync = 0;
      const at = yt.position();
      if (at === null) return;
      onControl?.(yt.paused() ? "pause" : "play", at, true);
    };

    tick();
    const timer = setInterval(() => {
      tick();
      syncTick();
    }, 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPresenter, onProgress, videoKind, playableUrl]);

  const describeMediaError = (el: HTMLVideoElement): string => {
    switch (el.error?.code) {
      case MediaError.MEDIA_ERR_ABORTED:
        return "Playback was cancelled.";
      case MediaError.MEDIA_ERR_NETWORK:
        return "The video could not be downloaded. The host may block direct linking.";
      case MediaError.MEDIA_ERR_DECODE:
        if (isIOSDevice() && /\.(mkv|webm)(?:[?#]|$)/i.test(videoName || "")) return t("layouts.videoFormatNotOnIOS");
        return "The file is corrupt, or uses a codec this browser cannot decode.";
      case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
        // Overwhelmingly the common case for a pasted link: the URL points at a
        // web page rather than a media file, so there is nothing to decode.
        // iPhone and iPad Safari cannot decode MKV, and most WebM — Chrome on
        // the presenter's machine plays them, so nothing looked wrong there.
        if (isIOSDevice() && /\.(mkv|webm)(?:[?#]|$)/i.test(videoName || "")) return t("layouts.videoFormatNotOnIOS");
        return "This link is not a playable video file. Direct links to .mp4/.webm work, as do YouTube, Vimeo and Google Drive links.";
      default:
        return "The video could not be played.";
    }
  };

  // Make sure we drop out of browser fullscreen if the user clicks the
  // header close button — otherwise the chrome would reappear with the
  // meeting still in fullscreen mode.
  const handleClose = () => {
    if (isFullscreen) exitFullscreen()
    onClose()
  };

  return (
    <div className="relative flex h-full w-full overflow-hidden">
      {/* Main Content Area - Video Player */}
      <div ref={mainRef} className="relative flex-1 min-h-0 flex flex-col bg-[#0f1322]">
        {/* Header Bar */}
        {/* The title side shrinks and truncates; the buttons never do — in full
            screen the mic and camera controls join them, and on a phone a long
            file name pushed exit and close off the right edge. Clear of the
            notch while full screen, as screen share's controls are. */}
        <div
          className="flex items-center justify-between gap-2 px-4 py-3 bg-[#1a1f2e] border-b border-white/10"
          style={isFullscreen ? {
            paddingTop: "max(12px, env(safe-area-inset-top))",
            paddingLeft: "max(16px, env(safe-area-inset-left))",
            paddingRight: "max(16px, env(safe-area-inset-right))",
          } : undefined}
        >
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <div className="flex shrink-0 items-center justify-center h-8 w-8 rounded-lg bg-cyan-500/20">
              <svg className="h-4 w-4 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <span className="min-w-0 truncate text-sm font-medium text-white/90">{videoName}</span>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {isFullscreen && fullscreenControls && (
              <div className="mr-2 flex shrink-0 items-center gap-2">{fullscreenControls}</div>
            )}
            <button
              onClick={toggleFullscreen}
              className="grid h-8 w-8 cursor-pointer place-items-center rounded-full text-white/70 hover:bg-card/10 hover:text-white transition"
              title={isFullscreen ? t("layouts.exitFullscreen") : t("layouts.enterFullscreen")}
              aria-label={isFullscreen ? t("layouts.exitFullscreen") : t("layouts.enterFullscreen")}
              aria-pressed={isFullscreen}
            >
              {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>
            {canClose && (
              <button
                onClick={handleClose}
                className="grid h-8 w-8 cursor-pointer place-items-center rounded-full text-white/70 hover:bg-card/10 hover:text-white transition"
                title={t("layouts.stopPresenting")}
                aria-label={t("layouts.stopPresenting")}
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>

        {/* Video Container */}
        <div className="relative flex-1 overflow-hidden bg-[#0f1322] flex items-center justify-center">
          {resolving ? (
            <div className="flex flex-col items-center justify-center gap-3 text-white/70">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-white/70" />
              <p className="text-sm">Loading shared file…</p>
            </div>
          ) : resolveError ? (
            <div className="flex flex-col items-center justify-center gap-2 px-8 text-center">
              <p className="text-sm text-white/80">{resolveError}</p>
            </div>
          ) : playableUrl && playableUrl !== "#" && videoKind === "embed" ? (
            <iframe
              // Keyed on the src so a different video gets a NEW element rather
              // than a reused one: the YouTube player binds to this node, and
              // rebinding a second player to the same iframe leaves two sets of
              // listeners on one video.
              key={embedSrc}
              ref={embedRef}
              onLoad={handshakeEmbed}
              // `start` puts a late joiner where the room is. YouTube and Vimeo
              // both read it off the embed URL; a provider that does not simply
              // begins at zero, which is where it began before.
              src={embedSrc}
              title={videoName}
              className="h-full w-full"
              style={{ maxHeight: 'calc(100% - 40px)', border: 0 }}
              // allow-list mirrors what YouTube/Vimeo/Drive players need; the
              // fullscreen button inside the embed is theirs, not ours.
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
            />
          ) : playableUrl && playableUrl !== "#" ? (
            <div className="relative flex h-full w-full items-center justify-center">
              <video
                ref={videoRef}
                src={playableUrl}
                // The presenter drives; a viewer gets no scrubber, because
                // their seeking would either desync them from the room or, if
                // it were broadcast, fight the presenter for the playhead.
                controls={isPresenter}
                // A shared video is for watching, not for taking away. The
                // presenter may be someone other than whoever uploaded it, and
                // Chrome's player menu and right-click both offer to save it.
                controlsList="nodownload"
                onContextMenu={(e) => e.preventDefault()}
                // Deliberately NO crossOrigin: plain playback does not need CORS,
                // and setting it makes the browser require CORS headers the host
                // may not send — which would break direct links that work today.
                className="max-w-full max-h-full object-contain"
                style={{ maxHeight: 'calc(100% - 40px)' }}
                // Inline, not the iPhone's own full-screen player, and start
                // fetching straight away rather than on first play.
                playsInline
                {...{ "webkit-playsinline": "true" }}
                preload="auto"
                onError={(e) => setMediaError(describeMediaError(e.currentTarget))}
                onLoadedData={() => setMediaError(null)}
                // Whatever the presenter does to their own player becomes the
                // room's instruction — no separate control bar to keep in sync
                // with the real playback state.
                onPlay={(e) => emit("play", e.currentTarget.currentTime)}
                onPause={(e) => emit("pause", e.currentTarget.currentTime)}
                onSeeked={(e) => emit("seek", e.currentTarget.currentTime)}
              />
              {!mediaError && needsTap && (
                <button
                  type="button"
                  onClick={() => {
                    const el = videoRef.current;
                    if (!el) return;
                    el.muted = false;
                    unmutedByUserRef.current = true;
                    setMutedForAutoplay(false);
                    void el.play().then(() => setNeedsTap(false), () => {});
                  }}
                  className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/50 text-white"
                >
                  <span className="grid h-16 w-16 place-items-center rounded-full bg-white/15 ring-1 ring-white/30">
                    <Play className="h-8 w-8" />
                  </span>
                  <span className="text-sm font-medium">{t("layouts.tapToPlay")}</span>
                </button>
              )}
              {!mediaError && !needsTap && mutedForAutoplay && (
                <button
                  type="button"
                  onClick={() => {
                    const el = videoRef.current;
                    if (!el) return;
                    el.muted = false;
                    unmutedByUserRef.current = true;
                    setMutedForAutoplay(false);
                  }}
                  className="absolute right-3 top-3 z-20 flex items-center gap-2 rounded-full bg-black/70 px-3 py-1.5 text-xs font-semibold text-white ring-1 ring-white/20"
                >
                  <VolumeX className="h-4 w-4" />
                  {t("layouts.tapToUnmute")}
                </button>
              )}
              {mediaError && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[#0f1322]/95 px-8 text-center">
                  <svg className="h-12 w-12 text-amber-400/80" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  </svg>
                  <p className="max-w-md text-sm text-white/80">{mediaError}</p>
                  <p className="max-w-md break-all text-xs text-white/40">{videoUrl}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center text-white/60">
              <svg className="h-16 w-16 mb-4 text-white/30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-lg font-medium">{t("layouts.videoPreview")}</p>
              <p className="text-sm">{videoName}</p>
            </div>
          )}

          {/* A viewer must not be able to touch the player.
              `controls=0` asks YouTube not to draw its bar, but the centre play
              button, the double-tap-to-skip zones and the fullscreen control
              are still live, and anything a viewer pressed there moved their
              copy out of step with the room. This swallows the clicks; the
              presenter's own frame is left alone. Below the badge and the
              close button in the header, both of which stay usable. */}
          {videoKind === "embed" && !isPresenter && !resolving && !resolveError &&
            playableUrl && playableUrl !== "#" && (
              <div className="absolute inset-0 z-10 cursor-default" aria-hidden="true" />
            )}

          {/* Presenter Info Badge */}
          <div className="absolute left-3 bottom-3 z-20 rounded-full bg-black/45 px-3 py-1 text-xs font-semibold text-white/80 ring-1 ring-white/10">
            {presenterName} ({isPresenter ? t("layouts.youPresenting") : t("layouts.presenting")})
          </div>

          {/* An embed gives the presenter no controls this code can observe —
              the player is inside a cross-origin iframe. These drive it by
              postMessage and broadcast the same instruction to the room, so
              everyone moves together. A viewer gets nothing: the embed's own
              controls are YouTube's, and there is no way to take them away, but
              nothing a viewer does there leaves their own tab. */}
          {videoKind === "embed" && isPresenter && playableUrl && playableUrl !== "#" && (
            <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full bg-black/70 px-2 py-1.5 ring-1 ring-white/15">
              <button
                onClick={() => {
                  // Relative to a real reading. Skipping from an estimate moved
                  // every viewer to a point the presenter was never at.
                  const now = trustedPosition();
                  if (now === null) return;
                  const at = Math.max(0, now - 10);
                  applyCommand("seek", at);
                  onControl?.("seek", at);
                }}
                className="grid h-8 w-8 cursor-pointer place-items-center rounded-full text-white/80 hover:bg-white/10 hover:text-white transition"
                title="Back 10 seconds"
                aria-label="Back 10 seconds"
              >
                <RotateCcw className="h-4 w-4" />
              </button>
              <button
                onClick={() => {
                  const at = trustedPosition();
                  applyCommand("play", at ?? localPosition());
                  // Sent without a position when the player cannot be read:
                  // everyone resumes where they are, rather than jumping.
                  onControl?.("play", at ?? UNKNOWN_POSITION);
                }}
                className="grid h-8 w-8 cursor-pointer place-items-center rounded-full text-white/80 hover:bg-white/10 hover:text-white transition"
                title="Play"
                aria-label="Play"
              >
                <Play className="h-4 w-4" />
              </button>
              <button
                onClick={() => {
                  const at = trustedPosition();
                  applyCommand("pause", at ?? localPosition());
                  onControl?.("pause", at ?? UNKNOWN_POSITION);
                }}
                className="grid h-8 w-8 cursor-pointer place-items-center rounded-full text-white/80 hover:bg-white/10 hover:text-white transition"
                title="Pause"
                aria-label="Pause"
              >
                <Pause className="h-4 w-4" />
              </button>
              <button
                onClick={() => {
                  const now = trustedPosition();
                  if (now === null) return;
                  const at = now + 10;
                  applyCommand("seek", at);
                  onControl?.("seek", at);
                }}
                className="grid h-8 w-8 cursor-pointer place-items-center rounded-full text-white/80 hover:bg-white/10 hover:text-white transition"
                title="Forward 10 seconds"
                aria-label="Forward 10 seconds"
              >
                <RotateCw className="h-4 w-4" />
              </button>
            </div>
          )}

          {localOnly && (
            <div className="absolute right-3 bottom-3 max-w-sm rounded-lg bg-amber-500/90 px-3 py-2 text-xs font-medium text-black/80">
              Only you can see this. An uploaded file stays on your machine —
              paste a link instead to share it with the room.
            </div>
          )}
        </div>
      </div>

      {!phoneLayout && (<>
      {/* Resizer Handle */}
      <div
        role="separator"
        aria-orientation="vertical"
        onPointerDown={(e) => {
          if (railDragRef) {
            railDragRef.current = { startX: e.clientX, startWidth: rightRailWidth }
          }
        }}
        className="relative mr-2 hidden w-3 cursor-col-resize select-none items-center justify-center rounded-full bg-card/5 hover:bg-card/10 lg:flex"
        title={t("layouts.resize")}
      >
        <div className="grid gap-1">
          <span className="h-1 w-1 rounded-full bg-card/40" />
          <span className="h-1 w-1 rounded-full bg-card/40" />
          <span className="h-1 w-1 rounded-full bg-card/40" />
        </div>
      </div>

      {/* Right Sidebar - Participant Tiles */}
      <aside className="min-h-0 shrink-0 overflow-hidden bg-transparent" style={{ width: rightRailWidth }}>
        <div ref={(node) => setRightRailEl(node)} className="relative h-full min-h-0 overflow-hidden">
          {rightRailPageCount > 1 ? (
            <>
              <button
                type="button"
                onClick={() => onSetRightRailPage((p) => Math.max(0, p - 1))}
                disabled={safeRightRailPage <= 0}
                className="absolute cursor-pointer left-1/2 top-2 z-10 grid h-9 w-9 -translate-x-1/2 place-items-center rounded-full bg-black/45 text-white/80 ring-1 ring-white/10 transition hover:bg-black/55 hover:text-white disabled:opacity-30"
                title={t("layouts.previous")}
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M18 15l-6-6-6 6" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => onSetRightRailPage((p) => Math.min(rightRailPageCount - 1, p + 1))}
                disabled={safeRightRailPage >= rightRailPageCount - 1}
                className="absolute cursor-pointer left-1/2 bottom-2 z-10 grid h-9 w-9 -translate-x-1/2 place-items-center rounded-full bg-black/45 text-white/80 ring-1 ring-white/10 transition hover:bg-black/55 hover:text-white disabled:opacity-30"
                title={t("layouts.next")}
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 9l6 6-6-6" />
                </svg>
              </button>
              <div className="absolute right-2 top-2 z-10 rounded-full bg-black/35 px-2 py-1 text-[11px] font-semibold text-white/70 ring-1 ring-white/10 tabular-nums">
                {safeRightRailPage + 1}/{rightRailPageCount}
              </div>
            </>
          ) : null}

          {railRowChunks.length === 0 ? (
            <div className="flex items-center justify-center h-full text-white/60 text-sm">
              {t("layouts.noParticipants")}
            </div>
          ) : (
            // The same rail as every other stage layout. This one used to draw
            // its own two-column grid that stretched tiles off 16:9 and kept
            // only the first two of each row — a third person in a row was
            // silently never shown.
            <RailTiles
              rows={railRowChunks}
              rowHeight={railRowH}
              renderTile={(p) => (
                <VideoTile
                  participant={p}
                  reaction={reactionFor(p?.id)}
                  isPinned={pinnedIds.includes(p.id)}
                  onPin={() => onPin(p.id)}
                  onPinForAll={onPinForAll}
                  pinnedForAllId={pinnedForAllId}
                  onMaximize={(el) => onMaximize(el)}
                  onOpenChat={() => onOpenChat(p.id)}
                  virtualBackgroundUrl={selectedBackground?.url ?? null}
                />
              )}
            />
          )}
        </div>
      </aside>
      </>)}
    </div>
  );
}
